import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache, WSQueryKey } from "@ws-react-query";

/**
 * Why a requirement's data-source document (a signature request, a shared file
 * request, an insurance monitor…) is absent from the cache.
 *
 * - `Ready` — the document is there, or the requirement has no data source at
 *   all, so there is nothing to wait for.
 * - `Pending` — the read has not resolved yet (never started, or in flight).
 * - `Error` — the read settled on a failure and there is no document to render.
 */
export enum RequirementDataSourceState {
  Ready = "Ready",
  Pending = "Pending",
  Error = "Error"
}

/**
 * Resolves *why* a requirement's data-source document is missing, per
 * requirement rather than per batch.
 *
 * The configs read their document straight out of the query cache
 * (`WSQueryCache.getQueryData`), where a pending read and a failed read are
 * indistinguishable — both yield `undefined`. Rendering "error loading" for
 * both is what made a cold-cache details page paint an amber error before any
 * request had settled, so callers ask here instead.
 *
 * Deliberately keyed off the requirement's own `dataSourceId` and not off an
 * aggregate over the batch: one requirement's failed document must not mark
 * every other row on the screen as broken, and some batches carry incidental
 * non-data-source reads whose failures are irrelevant here.
 */
export const selectorRequirementDataSourceState = ({
  requirement,
  getQueryKey
}: {
  requirement: PayeeRequirement;
  getQueryKey: (dataSourceId: string) => WSQueryKey;
}): RequirementDataSourceState => {
  const { dataSourceId } = requirement;

  if (!dataSourceId) {
    return RequirementDataSourceState.Ready;
  }

  const state = WSQueryCache.getQueryState(getQueryKey(dataSourceId));

  // No cache entry at all — the query has not been registered yet (first render
  // before `useQueries` mounts it). That is pending, never a failure.
  if (!state) {
    return RequirementDataSourceState.Pending;
  }

  if (state.data !== undefined) {
    return RequirementDataSourceState.Ready;
  }

  // `error` rather than `status`: a background refetch that fails leaves the
  // status on "success" while the data is gone (invalidation removed it), and
  // that is still an error for rendering purposes.
  if (state.status === "error" || state.error) {
    return RequirementDataSourceState.Error;
  }

  return RequirementDataSourceState.Pending;
};
